import { digObject } from '@domain-group/javascript-utils'

import type { UseFormPayload } from '@domain-group/react-hooks'

import type { EntitiesState } from '@redux/entities'

import useCampaign, { UseCampaignFormPayload } from '@hooks/useCampaign'
import useSelector from '@hooks/useSelector'
import useTeam from '@hooks/useTeam'
import useUsers from '@hooks/useUsers'

import type { agentOrTeamObject } from '@components/AgentOrTeamSearch'

import getTeamAgents from '@functions/team'

import type { CampaignModel } from '@models/types'

type RemoveAgentParams = {
  agentId: number,
  agentIds: number[],
  setEntityState: UseFormPayload['setEntityState'],
}

const removeAgent = (params: RemoveAgentParams) => {
  const {
    agentId, agentIds, setEntityState,
  } = params

  const updatedAgentIds = agentIds.filter(id => id !== agentId)
  setEntityState({ agent_ids: updatedAgentIds })
}

type RemoveTeamParams = {
  entityState: UseFormPayload['entityState'],
  setEntityState: UseFormPayload['setEntityState'],
  teamAgentIds?: number[],
}

const removeTeam = (params: RemoveTeamParams) => {
  const {
    entityState,
    setEntityState,
    teamAgentIds,
  } = params

  const agentIds = digObject(entityState, 'agent_ids', [])

  const updatedAgentIds = agentIds.filter((agentId: number) => !teamAgentIds?.includes(agentId))
  setEntityState({ agent_ids: [...updatedAgentIds], team_id: null })
}

type SelectAgentOrTeamParams = {
  agentIdsWithoutTeam: number[],
  agentOrTeam: agentOrTeamObject,
  entities: EntitiesState,
  entityState: UseFormPayload['entityState'],
  setEntityState: UseFormPayload['setEntityState'],
}

const selectAgentOrTeam = (params: SelectAgentOrTeamParams) => {
  const {
    agentIdsWithoutTeam,
    agentOrTeam,
    entities,
    entityState,
    setEntityState,
  } = params

  const agentIds = digObject(entityState, 'agent_ids', [])
  const { key } = agentOrTeam
  const [entityType, entityId] = key.split('-')
  const { team_id } = entityState

  // The maximum number of stand-alone assigned agents is 4
  if (agentIdsWithoutTeam.length === 4 && entityType === 'Agent'){
    const agentId = agentIdsWithoutTeam[0]
    const indexToRemove = agentIds.indexOf(agentId)

    if (indexToRemove !== -1) agentIds.splice(indexToRemove, 1)
  }

  // Selecting an Agent
  if (entityType === 'Agent'){
    setEntityState({ agent_ids: [...agentIds, Number(entityId)] })
  }

  // Selecting a Team
  if (entityType === 'Team'){
    const { teamMembers, teams, users } = entities
    const { teamAgentIds } = getTeamAgents({ team: teams[entityId], teamMembers, users })
    // check the agent ids of incoming team
    // if a team agent is already included as 'without a team', remove them and add the team agents
    const updatedAgentIds = agentIdsWithoutTeam.filter(id => !teamAgentIds?.includes(id))
    // @ts-expect-error TS2769
    const updatedTeamAndAgents = updatedAgentIds.concat(teamAgentIds)

    if (!team_id){
      setEntityState({ agent_ids: [...updatedTeamAndAgents], team_id: Number(entityId) })
    }

    // remove existing team & team members before adding new team
    if (team_id){
      removeTeam({ entityState, setEntityState, teamAgentIds })
      setEntityState({ agent_ids: [...updatedTeamAndAgents], team_id: Number(entityId) })
    }
  }
}

type UseAssignCampaignTeamOrAgentsParams = {
  campaign?: Partial<CampaignModel>,
  campaignFormPayload: UseCampaignFormPayload,
}

const useAssignCampaignTeamOrAgents = (params: UseAssignCampaignTeamOrAgentsParams) => {
  const { campaign: initCampaign = {}, campaignFormPayload } = params

  const campaignPayload = useCampaign(initCampaign)
  const {
    entityState,
    entityState: {
      team_id,
    },
    setEntityState,
  } = campaignFormPayload

  const agentIds = digObject(entityState, 'agent_ids', [])

  const teamPayload = useTeam({ id: team_id })

  useUsers({
    performHttpRequests: true,
    requestOptions: {
      agents_only: true,
      include_image_sizes: true,
      include_user_association: true,
      include_user_image: true,
    },
  })

  const entities = useSelector(reduxState => reduxState.entities)
  const { teamMembers, teams, users } = entities

  const { teamAgentIds } = getTeamAgents({ team: teams[team_id], teamMembers, users })

  // For the Additional Agents Section. These are the ids of the agents that are assigned but not part of a team.
  const agentIdsWithoutTeam = agentIds.filter((agentId: number) => !teamAgentIds?.includes(agentId))

  const hasAssignedAgents = !!agentIdsWithoutTeam.length
  const hasAssignedTeam = !!team_id

  return {
    agentIdsWithoutTeam,
    campaignFormPayload,
    campaignPayload,
    callbacks: {
      removeAgent: (agentId: number) => removeAgent({ agentId, agentIds, setEntityState }),
      removeTeam: () => removeTeam({ entityState, setEntityState, teamAgentIds }),
      selectAgentOrTeam: (agentOrTeam: agentOrTeamObject) => selectAgentOrTeam({
        agentIdsWithoutTeam, agentOrTeam, entities, entityState, setEntityState,
      }),
      setEntityState,
    },
    hasAssignedAgents,
    hasAssignedTeam,
    teamPayload,
  }
}

export type UseAssignCampaignTeamOrAgentsPayload = ReturnType<typeof useAssignCampaignTeamOrAgents>

export default useAssignCampaignTeamOrAgents
